/**
 * Payments page - manage supplier payment exports
 * Shows orders ready for payment and allows CSV export
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { wywolajFunkcje } from '../services/funkcje';
import { Order, toDate } from '../types';
import { SilviaLoader } from '../components/SilviaLoader';
import '../styles/Payments.css';

interface PaymentOrder {
  id: string;
  woocommerce_order_id: string;
  supplier_order_id: string;
  supplier_total_cost: number;
  order_date: string;
  customer_name: string;
  customer_company?: string;
  items_count: number;
}

type TabType = 'pending' | 'exported';

export function Payments() {
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [pendingOrders, setPendingOrders] = useState<PaymentOrder[]>([]);
  const [exportedOrders, setExportedOrders] = useState<PaymentOrder[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [lastExportBatchId, setLastExportBatchId] = useState<string | null>(null);

  // Fetch orders from Firestore
  useEffect(() => {
    setLoading(true);

    // Query orders that have supplier_order_id (successfully ordered)
    const ordersQuery = query(
      collection(db, 'orders'),
      where('supplier_order_id', '!=', null)
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const pending: PaymentOrder[] = [];
      const exported: PaymentOrder[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data() as Order;
        
        const cost = data.supplier_total_cost || data.total_amount || 0;
        
        const paymentOrder: PaymentOrder = {
          id: doc.id,
          woocommerce_order_id: data.woocommerce_order_id,
          supplier_order_id: data.supplier_order_id!,
          supplier_total_cost: cost,
          order_date: data.created_at ? toDate(data.created_at).toISOString() : '',
          customer_name: `${data.customer.first_name} ${data.customer.last_name}`,
          customer_company: data.customer.company,
          items_count: data.items?.length || 0,
        };

        if (data.supplier_payment_exported) {
          exported.push(paymentOrder);
        } else if (cost > 0) {
          pending.push(paymentOrder);
        }
      });

      // Sort by order date
      pending.sort((a, b) => a.order_date.localeCompare(b.order_date));
      exported.sort((a, b) => b.order_date.localeCompare(a.order_date)); // Newest first for exported

      setPendingOrders(pending);
      setExportedOrders(exported);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Calculate totals
  const pendingTotal = pendingOrders.reduce((sum, o) => sum + o.supplier_total_cost, 0);
  const exportedTotal = exportedOrders.reduce((sum, o) => sum + o.supplier_total_cost, 0);
  const selectedTotal = Array.from(selectedOrders).reduce((sum, id) => {
    const order = pendingOrders.find(o => o.id === id);
    return sum + (order?.supplier_total_cost || 0);
  }, 0);

  // Handle order selection
  const handleSelectOrder = (orderId: string, checked: boolean) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(orderId);
      } else {
        newSet.delete(orderId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(new Set(pendingOrders.map(o => o.id)));
    } else {
      setSelectedOrders(new Set());
    }
  };

  // Export selected orders
  const handleExport = async () => {
    if (selectedOrders.size === 0) {
      alert('Wybierz zamówienia do eksportu');
      return;
    }

    const confirmMsg = `Czy na pewno chcesz wyeksportować ${selectedOrders.size} zamówień na łączną kwotę ${selectedTotal.toFixed(2).replace('.', ',')} PLN?\n\nPo eksporcie zamówienia zostaną oznaczone jako "opłacone" i nie będą się już pojawiać na tej liście.`;
    if (!confirm(confirmMsg)) {
      return;
    }

    setExporting(true);

    try {
      const response = await wywolajFunkcje('exportOrdersForPayment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderIds: Array.from(selectedOrders),
          markAsExported: true,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Download CSV
        const blob = new Blob([data.csv_content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `platnosci_dostawca_${data.batch_id}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setLastExportBatchId(data.batch_id);
        setSelectedOrders(new Set());

        alert(`✅ Wyeksportowano ${data.orders_count} zamówień\nID partii: ${data.batch_id}\nŁączna kwota: ${data.total_amount.toFixed(2).replace('.', ',')} PLN`);
      } else {
        alert(`Błąd eksportu: ${data.error}`);
      }
    } catch (error) {
      alert(`Błąd połączenia: ${error instanceof Error ? error.message : 'Nieznany błąd'}`);
    } finally {
      setExporting(false);
    }
  };

  // Undo last export
  const handleUndoExport = async () => {
    if (!lastExportBatchId) {
      alert('Brak ostatniego eksportu do cofnięcia');
      return;
    }

    if (!confirm(`Czy na pewno chcesz cofnąć eksport partii ${lastExportBatchId}?\n\nZamówienia wrócą na listę "Do opłacenia".`)) {
      return;
    }

    try {
      const response = await wywolajFunkcje('undoPaymentExport', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          batchId: lastExportBatchId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ Cofnięto eksport ${data.updated_count} zamówień`);
        setLastExportBatchId(null);
      } else {
        alert(`Błąd: ${data.error}`);
      }
    } catch (error) {
      alert(`Błąd połączenia: ${error instanceof Error ? error.message : 'Nieznany błąd'}`);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return amount.toFixed(2).replace('.', ',') + ' zł';
  };

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pl-PL');
  };

  const currentOrders = activeTab === 'pending' ? pendingOrders : exportedOrders;

  if (loading) {
    return (
      <div className="payments-page">
        <SilviaLoader text="Ładowanie zamówień..." />
      </div>
    );
  }

  return (
    <div className="payments-page">
      <div className="payments-header">
        <h1>💳 Płatności do dostawcy</h1>
        <p className="subtitle">Zarządzaj płatnościami za zamówienia u dostawcy</p>
      </div>

      {/* Summary cards */}
      <div className="payments-summary">
        <div className="summary-card pending">
          <div className="summary-icon">🛒</div>
          <div className="summary-content">
            <div className="summary-label">Do opłacenia</div>
            <div className="summary-value">{formatCurrency(pendingTotal)}</div>
            <div className="summary-count">{pendingOrders.length} zamówień</div>
          </div>
        </div>
        <div className="summary-card exported">
          <div className="summary-icon">✅</div>
          <div className="summary-content">
            <div className="summary-label">Opłacone</div>
            <div className="summary-value">{formatCurrency(exportedTotal)}</div>
            <div className="summary-count">{exportedOrders.length} zamówień</div>
          </div>
        </div>
        {selectedOrders.size > 0 && (
          <div className="summary-card selected">
            <div className="summary-icon">📋</div>
            <div className="summary-content">
              <div className="summary-label">Zaznaczono</div>
              <div className="summary-value">{formatCurrency(selectedTotal)}</div>
              <div className="summary-count">{selectedOrders.size} zamówień</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="payments-tabs">
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          🛒 Do opłacenia ({pendingOrders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'exported' ? 'active' : ''}`}
          onClick={() => setActiveTab('exported')}
        >
          ✅ Opłacone ({exportedOrders.length})
        </button>
      </div>

      {/* Actions */}
      {activeTab === 'pending' && (
        <div className="payments-actions">
          <button 
            className="btn-export"
            onClick={handleExport}
            disabled={selectedOrders.size === 0 || exporting}
          >
            {exporting ? '⏳ Eksportuję...' : `📥 Eksportuj do CSV (${selectedOrders.size})`}
          </button>
          {lastExportBatchId && (
            <button 
              className="btn-undo"
              onClick={handleUndoExport}
            >
              ↩️ Cofnij ostatni eksport
            </button>
          )}
        </div>
      )}

      {/* Orders table */}
      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              {activeTab === 'pending' && (
                <th className="col-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedOrders.size === pendingOrders.length && pendingOrders.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}
              <th className="col-wc-order">Zamówienie WC</th>
              <th className="col-supplier-order">Nr dostawcy</th>
              <th className="col-payment-status">Status płatności</th>
              <th className="col-date">Data</th>
              <th className="col-customer">Klient</th>
              <th className="col-items">Pozycje</th>
              <th className="col-amount">Kwota</th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.length === 0 ? (
              <tr>
                <td colSpan={activeTab === 'pending' ? 8 : 7} className="empty-message">
                  {activeTab === 'pending' 
                    ? '🎉 Brak zamówień do opłacenia' 
                    : 'Brak wyeksportowanych zamówień'}
                </td>
              </tr>
            ) : (
              currentOrders.map(order => (
                <tr 
                  key={order.id} 
                  className={`${selectedOrders.has(order.id) ? 'selected' : ''} clickable-row`}
                  onClick={() => window.location.href = `/orders/${order.id}`}
                  style={{ cursor: 'pointer' }}
                >
                  {activeTab === 'pending' && (
                    <td className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedOrders.has(order.id)}
                        onChange={(e) => handleSelectOrder(order.id, e.target.checked)}
                      />
                    </td>
                  )}
                  <td className="col-wc-order">
                    <Link to={`/orders/${order.id}`} onClick={(e) => e.stopPropagation()}>
                      #{order.woocommerce_order_id}
                    </Link>
                  </td>
                  <td className="col-supplier-order">
                    <span className="supplier-order-badge">{order.supplier_order_id}</span>
                  </td>
                  <td className="col-payment-status">
                    {activeTab === 'pending' ? (
                      <span className="payment-status-badge pending">⏳ Do opłacenia</span>
                    ) : (
                      <span className="payment-status-badge paid">✅ Opłacone</span>
                    )}
                  </td>
                  <td className="col-date">{formatDate(order.order_date)}</td>
                  <td className="col-customer">
                    <div className="customer-name">{order.customer_name}</div>
                    {order.customer_company && (
                      <div className="customer-company">{order.customer_company}</div>
                    )}
                  </td>
                  <td className="col-items">{order.items_count}</td>
                  <td className="col-amount">{formatCurrency(order.supplier_total_cost)}</td>
                </tr>
              ))
            )}
          </tbody>
          {currentOrders.length > 0 && (
            <tfoot>
              <tr className="total-row">
                <td colSpan={activeTab === 'pending' ? 7 : 6} className="total-label">
                  RAZEM:
                </td>
                <td className="total-amount">
                  {formatCurrency(activeTab === 'pending' ? pendingTotal : exportedTotal)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
